<template lang="pug">
nav
  nav.appNavigation(:class="{ 'appNavigation--scrolled': scrolled }")
    .mobileMenu(:class="{ 'show': mobileShow }")
      router-link(v-for="link in links" :key="link.index" :to="link.slug" v-html="link.title" @click="mobileShow = !mobileShow")
    .appNavigation__inner
      h1.appNavigation__logo(class="animated" :class="logoClass")
        router-link(to="/")
          img(:src="require('@/assets/images/BBDO_ATLANTA_logo.svg')")
      .appNavigation__links.show.show--scrolled
        router-link.hover-shift(v-for="link in links" :key="link.index" :to="link.slug" class="animated" :class="menuClass" v-html="link.title")
      .appNavigation__button(@click="toggleMenuButton")
        hamburger-button.hide(:class="{ 'hide--scrolled': showHamburger }" :close="mobileShow")
</template>

<script>
import { mapState } from 'vuex'
import debug from 'debug'
import HamburgerButton from '@/components/HamburgerButton'
import { throttle } from 'throttle-debounce'

let log = debug('component:AppNavigation')

export default {
  name: 'appNavigation',
  props: {
    text: {
      type: String,
      required: false,
    },
  },
  computed: mapState({
    links: (state) => state.navigation,
    isMobile: (state) => state.isMobile,
  }),
  data () {
    return {
      scrollThreshold: 70,
      showMenu: false,
      menuClass: 'fadeInRight',
      logoClass: 'fadeInLeft',
      forceDisplay: false,
      mobileShow: false,
      scrolling: false,
      scrolled: false,
      showHamburger: false,
      lastScrollTop: 0,
      doSnapScroll: true,
      scrollingOptions: {
        offset: -72,
        onStart: (el) => {
          this.scrolling = true
        },
        onDone: (el) => {
          setTimeout(function () {
            document.querySelector('.appNavigation').style.background =
              'rgba(255,255,255,1)'
          }, 50)
          setTimeout(function () {
            this.scrolling = false
          }, 5000)
        },
        onCancel: () => {
          setTimeout(function () {
            this.scrolling = false
          }, 5000)
        },
      },
    }
  },
  watch: {
    $route () {
      this.mobileShow = false
      document.querySelector('.appNavigation').style.background =
        'rgba(255,255,255,1)'
    },
  },
  mounted () {
    log('Mounted')
    this.lastScrollTop = window.scrollY
  },
  created () {
    window.addEventListener('scroll', throttle(100, this.onScroll))
  },
  destroyed () {
    window.removeEventListener('scroll', throttle(100, this.onScroll))
  },
  methods: {
    onScroll (e) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      scrollTop > this.lastScrollTop
        ? this.onScrollDown(scrollTop)
        : this.onScrollUp()
      this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop // For Mobile or negative scrolling
      let vh = window.innerHeight
      let multiplier = window.innerWidth > 768 ? 2 : 1
      // console.debug('scrollTop', scrollTop)

      if (scrollTop === 0) {
        this.scrollReveal = false
        this.displayMenu()
        this.showHamburger = false
        this.scrolling = false
      } else if (
        scrollTop > vh / 2 &&
        scrollTop < vh * multiplier &&
        this.scrolling === false &&
        this.doSnapScroll === true
      ) {
        this.scrolling = false
      }
    },

    onScrollUp () {
      this.scrolled = false
      this.scrollReveal = true
      this.showHamburger = false
      this.displayMenu()
      document.querySelector('.appNavigation').style.background =
        'rgba(255,255,255,1)'
    },

    onScrollDown () {
      this.scrolled = true
      this.scrollReveal = true
      this.showHamburger = true
      if (!this.isMobile) {
        this.mobileShow = false
        this.forceDisplay = false
      }
      this.hideMenu()
      document.querySelector('.appNavigation').style.background =
        'rgba(255,255,255,0.4)'
    },
    displayMenu () {
      this.menuClass = 'fadeInRight'
      this.showMenu = true
    },
    hideMenu () {
      this.menuClass = 'fadeOutRight'
      this.showMenu = false
    },
    toggleMenu () {
      if (this.menuClass === 'fadeInRight') {
        this.hideMenu()
      } else {
        this.displayMenu()
      }
    },
    toggleMenuButton () {
      this.forceDisplay = !this.forceDisplay
      this.mobileShow = !this.mobileShow
      if (this.forceDisplay) {
        this.menuClass = 'fadeInRight'
        this.showMenu = true
        document.querySelector('.appNavigation').style.background =
          'rgba(255,255,255,1)'
      } else {
        this.menuClass = 'fadeOutRight'
        this.showMenu = false
        document.querySelector('.appNavigation').style.background =
          'rgba(255,255,255,0.4)'
      }
    },
  },
  components: {
    HamburgerButton,
  },
}
</script>

<style scoped lang="scss">
.hide {
  opacity: 0;
  transition: all ease 0.5s;

  &:not(.hide--scrolled) {
    transition-delay: 0;
  }

  &--scrolled {
    opacity: 1;
    transition-delay: 300ms;
  }

  @media #{$mobile} {
    opacity: 1 !important;
  }
}

.appNavigation {
  position: fixed;
  padding: 0 $desktopMargins;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  height: 72px;
  background-color: $white;
  transition: background-color 0.5s;

  .mobileMenu {
    position: fixed;
    opacity: 0;
    margin-top: -72px;
    top: 72px;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: $white;
    transition: 300ms all;
    pointer-events: none;
    z-index: 1;

    &.show {
      @media #{$mobile} {
        opacity: 1;
        pointer-events: all;
      }
    }

    &.hide {
      opacity: 0;
      transition: all ease 0.5s;
    }

    a {
      padding: 15px 0;
      font-size: 2.5em;
      font-weight: 400;
      color: $siteDark;

      &:hover {
        color: $brandPrimary;
      }

      &:not(:first-of-type).router-link-active {
        color: $brandPrimary;
      }

      &.router-link-exact-active {
        color: $brandPrimary;
      }
    }
  }

  &--scrolled {
    background-color: rgba($white, 0.4);
  }

  &::after {
    content: '';
    bottom: 0;
    left: 0;
    height: 2px;
    width: 100%;
    background-color: $siteOffWhite;
  }

  &__inner {
    position: relative;
    height: 100%;
    width: 100%;
    max-width: $grid-max-width;
    display: flex;
    align-items: center;
    margin: 0 auto;
  }

  &__logo {
    height: 54%;
    position: relative;
    margin: 0;
    z-index: 10000;
    img {
      height: 100%;
      display: block;
    }
  }

  &__links {
    font-size: 10px;
    text-transform: uppercase;
    flex: 1;
    text-align: right;

    @media #{$mobile} {
      display: none;
    }

    a {
      margin-left: 40px;
      text-decoration: none;
      position: relative;
      display: inline-block;

      &.fadeOutRight {
        pointer-events: none;
      }

      &:nth-of-type(5) {
        animation-delay: 100ms;
      }
      &:nth-of-type(4) {
        animation-delay: 200ms;
      }
      &:nth-of-type(3) {
        animation-delay: 300ms;
      }
      &:nth-of-type(2) {
        animation-delay: 400ms;
      }
      &:nth-of-type(1) {
        animation-delay: 500ms;
      }
    }
  }

  &__button {
    margin-left: 40px;
    z-index: 100;

    .close {
      font-size: 2em;
      top: 20px;
      z-index: 27;
      position: absolute;
      right: 28px;
    }

    @media #{$mobile} {
      opacity: 1 !important;
      margin-left: auto;
    }

    .hamburgerButton {
      @media #{$mobile} {
        opacity: 1 !important;
      }
    }
  }
}

.reveal {
  position: fixed;
  visibility: visible;
  overflow: hidden;
  background-size: 100vw;
  background-position: top;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  top: 70px;
  z-index: 0;
  transition: height ease 0.5s;

  &--scrolled {
    height: 0;
  }
}
.wrapper {
  video {
    position: sticky;
    height: 100vh;
    position: absolute;
    top: 100%;
    width: 100%;
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    text {
      font-family: 'Gotham', sans-serif;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 240px;
      text-anchor: middle;
    }

    > rect {
      -webkit-mask: url(#mask);
      mask: url(#mask);
    }

    rect {
      fill: rgba(255, 255, 255, 1);
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 2s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
